// src/app/features/products/productSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { productService } from "./productService";

/* =========================================================
   Thunks
========================================================= */
export const getAllProducts = createAsyncThunk("product/get", async (data, thunkAPI) => {
  try {
    return await productService.getProducts(data);
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || "Failed to load products";
    return thunkAPI.rejectWithValue(msg);
  }
});



export const getAProduct = createAsyncThunk("product/getAProduct", async (slug, thunkAPI) => {
  try {
    return await productService.getSingleProduct(slug);
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || "Failed to fetch product";
    return thunkAPI.rejectWithValue(msg);
  }
});

export const addToWishlists = createAsyncThunk("product/wishlist", async (prodId, thunkAPI) => {
  try {
    return await productService.addToWishlist(prodId);
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || "Failed to add to wishlist";
    return thunkAPI.rejectWithValue(msg);
  }
});

export const addRating = createAsyncThunk("product/rating", async (data, thunkAPI) => {
  try {
    return await productService.rateProduct(data);
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || "Failed to add rating";
    return thunkAPI.rejectWithValue(msg);
  }
});

/* =========================================================
   Initial State
========================================================= */
const initialState = {
  products: [],
  filteredProducts: [],

  // local-only search term
  searchTerm: "",

  singleProduct: null,
  addToWishlists: null,
  rating: null,

  // customer-side selected variant state
  selectedColor: null,
  selectedQty: 1,

  // pagination
  total: 0,
  page: 1,
  limit: 1000,
  pages: 0,
  hasNextPage: false,
  hasPrevPage: false,

  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
};

/* =========================================================
   Helpers
========================================================= */
const norm = (v) => String(v ?? "").toLowerCase().trim();

const idStr = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return String(v?._id ?? v?.id ?? "");
  return String(v);
};

const brandIdStr = (b) => {
  if (!b) return "";
  if (typeof b === "object") return idStr(b);
  return String(b);
};

const brandTitleNorm = (b) => {
  if (!b) return "";
  if (typeof b === "object") {
    return norm(b?.title ?? b?.name ?? "");
  }
  return norm(b);
};

const safeNum = (v, fallback = null) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const getCategoryIds = (product) => {
  const raw = product?.category;

  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw.map((c) => idStr(c)).filter(Boolean).map(String);
  }

  const single = idStr(raw);
  return single ? [String(single)] : [];
};

const getColorIds = (product) => {
  const raw = product?.color;

  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw.map((c) => idStr(c)).filter(Boolean).map(String);
  }

  const single = idStr(raw);
  return single ? [String(single)] : [];
};

const extractProductsFromPayload = (payload) => {
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractMetaFromPayload = (payload, fallbackList = []) => {
  const meta = payload?.meta;

  if (meta && typeof meta === "object") {
    return {
      total: Number(meta.total ?? fallbackList.length ?? 0),
      page: Number(meta.page ?? 1),
      limit: Number(meta.limit ?? 1000),
      pages: Number(meta.pages ?? 0),
      hasNextPage: Boolean(meta.hasNextPage),
      hasPrevPage: Boolean(meta.hasPrevPage),
    };
  }

  return {
    total: Number(payload?.total ?? fallbackList.length ?? 0),
    page: Number(payload?.page ?? 1),
    limit: Number(payload?.limit ?? 1000),
    pages: Number(payload?.pages ?? 0),
    hasNextPage: Boolean(payload?.hasNextPage),
    hasPrevPage: Boolean(payload?.hasPrevPage),
  };
};

const getColorVariantByColor = (product, colorId) => {
  if (!product || !colorId) return null;
  const variants = Array.isArray(product?.colorVariants) ? product.colorVariants : [];
  return variants.find((v) => String(v?.color?._id || v?.color) === String(colorId)) || null;
};

/**
 * Price resolution logic aligned with backend:
 * 1. selected color variant overrides product pricing where present
 * 2. inherited product discounted pricing is used ONLY if still valid
 *    for the selected variant/base price
 * 3. discountedPrice wins when discountMinQty is reached
 * 4. otherwise normal/base price
 */
export const resolveProductPricing = (product, colorId = null, qty = 1) => {
  const q = Math.max(1, Number(qty || 1));

  const basePrice = safeNum(product?.price, 0);
  const productDiscountedPrice = safeNum(product?.discountedPrice, null);
  const productDiscountMinQty = safeNum(product?.discountMinQty, null);

  const variant = getColorVariantByColor(product, colorId);

  const finalBasePrice =
    variant?.price !== null && variant?.price !== undefined
      ? safeNum(variant.price, basePrice)
      : basePrice;

  const finalDiscountedPrice =
    variant?.discountedPrice !== null && variant?.discountedPrice !== undefined
      ? safeNum(variant.discountedPrice, null)
      : productDiscountedPrice !== null && productDiscountedPrice < finalBasePrice
      ? productDiscountedPrice
      : null;

  const finalDiscountMinQty =
    finalDiscountedPrice !== null
      ? variant?.discountMinQty !== null && variant?.discountMinQty !== undefined
        ? safeNum(variant.discountMinQty, null)
        : productDiscountMinQty
      : null;

  let unitPrice = finalBasePrice;
  let source = "base";

  if (
    finalDiscountedPrice !== null &&
    finalDiscountMinQty !== null &&
    q >= finalDiscountMinQty
  ) {
    unitPrice = finalDiscountedPrice;
    source = "discount";
  }

  const quantityAvailable =
    variant?.quantity !== null && variant?.quantity !== undefined
      ? safeNum(variant.quantity, 0)
      : safeNum(product?.quantity, 0);

  const images =
    Array.isArray(variant?.images) && variant.images.length > 0
      ? variant.images
      : Array.isArray(product?.images)
      ? product.images
      : [];

  return {
    colorId: colorId || null,
    variant: variant || null,
    basePrice: finalBasePrice,
    discountedPrice: finalDiscountedPrice,
    discountMinQty: finalDiscountMinQty,
    unitPrice,
    totalPrice: Number(unitPrice) * q,
    quantityAvailable,
    images,
    source,
    hasColorOverride:
      variant?.price != null ||
      variant?.discountedPrice != null ||
      variant?.quantity != null ||
      (Array.isArray(variant?.images) && variant.images.length > 0),
  };
};

const applyLocalSearch = (list, term) => {
  const q = norm(term);
  if (!q) return list;

  return (Array.isArray(list) ? list : []).filter((p) => {
    const title = norm(p?.title);
    const slug = norm(p?.slug);
    const desc = norm(p?.description);

    const brandVal =
      typeof p?.brand === "object" && p?.brand
        ? String(p?.brand?.title ?? p?.brand?.name ?? p?.brand?._id ?? "")
        : String(p?.brand || "");

    const brand = norm(brandVal);
    const tags = Array.isArray(p?.tags) ? norm(p.tags.join(" ")) : "";

    // include color names in local search if product is populated
    const colorNames = Array.isArray(p?.color)
      ? norm(
          p.color
            .map((c) => (typeof c === "object" ? c?.name ?? c?.title ?? "" : ""))
            .join(" ")
        )
      : "";

    const categoryNames = Array.isArray(p?.category)
      ? norm(
          p.category
            .map((c) => (typeof c === "object" ? c?.title ?? c?.name ?? "" : ""))
            .join(" ")
        )
      : "";

    return (
      title.includes(q) ||
      slug.includes(q) ||
      desc.includes(q) ||
      brand.includes(q) ||
      tags.includes(q) ||
      colorNames.includes(q) ||
      categoryNames.includes(q)
    );
  });
};

/* =========================================================
   Slice
========================================================= */
export const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    setFilteredProducts: (state, action) => {
      state.filteredProducts = Array.isArray(action.payload) ? action.payload : [];
    },

    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload || "";
      state.filteredProducts = applyLocalSearch(state.products, state.searchTerm);
    },

    filterProductsLocal: (state) => {
      state.filteredProducts = applyLocalSearch(state.products, state.searchTerm);
    },

    resetSingleProduct: (state) => {
      state.singleProduct = null;
      state.selectedColor = null;
      state.selectedQty = 1;
    },

    resetProductsState: (state) => {
      state.products = [];
      state.filteredProducts = [];
      state.total = 0;
      state.page = 1;
      state.limit = 1000;
      state.pages = 0;
      state.hasNextPage = false;
      state.hasPrevPage = false;
      state.isError = false;
      state.isSuccess = false;
      state.isLoading = false;
      state.message = "";
      state.searchTerm = "";
      state.singleProduct = null;
      state.addToWishlists = null;
      state.rating = null;
      state.selectedColor = null;
      state.selectedQty = 1;
    },

    setSelectedColor: (state, action) => {
      state.selectedColor = action.payload || null;
    },

    setSelectedQty: (state, action) => {
      const qty = Number(action.payload);
      state.selectedQty = Number.isFinite(qty) && qty > 0 ? qty : 1;
    },
  },

  extraReducers: (builder) => {
    builder
      /* =========================
         GET ALL PRODUCTS
      ========================= */
      .addCase(getAllProducts.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(getAllProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;

        const list = extractProductsFromPayload(action.payload);
        const meta = extractMetaFromPayload(action.payload, list);

        state.products = list;
        state.filteredProducts = applyLocalSearch(list, state.searchTerm);

        state.total = Number(meta.total ?? list.length ?? 0);
        state.page = Number(meta.page ?? 1);
        state.limit = Number(meta.limit ?? state.limit ?? 1000);
        state.pages = Number(meta.pages ?? 0);
        state.hasNextPage = Boolean(meta.hasNextPage);
        state.hasPrevPage = Boolean(meta.hasPrevPage);
      })
      .addCase(getAllProducts.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.payload || "Failed to load products";
      })

      /* =========================
         WISHLIST
      ========================= */
      .addCase(addToWishlists.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(addToWishlists.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.addToWishlists = action.payload;
        state.message = "Product Added to Wishlist!";
      })
      .addCase(addToWishlists.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.payload || "Failed to add to wishlist";
      })

      /* =========================
         GET SINGLE PRODUCT
      ========================= */
      .addCase(getAProduct.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(getAProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.singleProduct = action.payload;
        state.message = "Product Fetched Successfully";

        // auto-select first available color for customer-side price/image switching
        const colors = Array.isArray(action.payload?.color) ? action.payload.color : [];
        if (colors.length > 0) {
          const firstColor = colors[0]?._id || colors[0];
          state.selectedColor = firstColor ? String(firstColor) : null;
        } else {
          state.selectedColor = null;
        }

        state.selectedQty = 1;
      })
      .addCase(getAProduct.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.payload || "Failed to fetch product";
      })

      /* =========================
         RATING
      ========================= */
      .addCase(addRating.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(addRating.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.rating = action.payload;
        state.message = "Rating Added Successfully";
        toast.success("Rating Added Successfully!");
      })
      .addCase(addRating.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.isSuccess = false;
        state.message = action.payload || "Failed to add rating";
      });
  },
});

/* =========================================================
   Selectors
========================================================= */
export const selectUniqueCategories = createSelector(
  (state) => (Array.isArray(state.product.products) ? state.product.products : []),
  (state) => (Array.isArray(state.category.categories) ? state.category.categories : []),
  (products, categories) => {
    const categoriesMap = new Map();

    products.forEach((product) => {
      const cats = getCategoryIds(product);

      cats.forEach((catId) => {
        const category = categories.find((c) => String(c._id) === String(catId));
        if (category && !categoriesMap.has(category._id)) {
          categoriesMap.set(category._id, {
            _id: category._id,
            title: category.title || "No Title",
            images: category.images || [],
          });
        }
      });
    });

    return Array.from(categoriesMap.values());
  }
);

export const selectSingleProduct = (state) => state.product.singleProduct;
export const selectSelectedColor = (state) => state.product.selectedColor;
export const selectSelectedQty = (state) => state.product.selectedQty;

export const selectSingleProductPricing = createSelector(
  [selectSingleProduct, selectSelectedColor, selectSelectedQty],
  (product, selectedColor, selectedQty) => {
    if (!product) return null;
    return resolveProductPricing(product, selectedColor, selectedQty);
  }
);

export const selectSingleProductDisplayImages = createSelector(
  [selectSingleProductPricing],
  (pricing) => {
    if (!pricing) return [];
    return Array.isArray(pricing.images) ? pricing.images : [];
  }
);

export const selectSingleProductAvailableQty = createSelector(
  [selectSingleProductPricing],
  (pricing) => {
    if (!pricing) return 0;
    return Number(pricing.quantityAvailable || 0);
  }
);

export const selectSingleProductColors = createSelector(
  [selectSingleProduct],
  (product) => {
    if (!product) return [];
    return Array.isArray(product.color) ? product.color : [];
  }
);

export const selectSingleProductColorVariants = createSelector(
  [selectSingleProduct],
  (product) => {
    if (!product) return [];
    return Array.isArray(product.colorVariants) ? product.colorVariants : [];
  }
);

/* =========================================================
   Thunk Filter (LOCAL FILTERS ONLY)
========================================================= */
export const filterProducts =
  ({ sort, tags, brand, category, minPrice, maxPrice, color } = {}) =>
  (dispatch, getState) => {
    const products = Array.isArray(getState().product.products) ? getState().product.products : [];
    let filtered = [...products];

    const safeTags = Array.isArray(tags) ? tags.map(norm).filter(Boolean) : [];
    if (safeTags.length > 0) {
      filtered = filtered.filter((p) => {
        const ptags = Array.isArray(p?.tags) ? p.tags.map(norm) : [];
        return safeTags.every((t) => ptags.includes(t));
      });
    }

    if (brand) {
      const wantedId = String(brand);
      const wantedTitle = norm(brand);

      filtered = filtered.filter((p) => {
        const pb = p?.brand;
        const pbId = brandIdStr(pb);
        const pbTitle = brandTitleNorm(pb);
        return String(pbId) === wantedId || (pbTitle && pbTitle === wantedTitle);
      });
    }

    if (category) {
      filtered = filtered.filter((p) => getCategoryIds(p).includes(String(category)));
    }

    const safeColors = Array.isArray(color) ? color.map(String) : [];
    if (safeColors.length > 0) {
      filtered = filtered.filter((p) => {
        const pc = getColorIds(p);
        return pc.some((c) => safeColors.includes(String(c)));
      });
    }

    if (minPrice !== null && minPrice !== undefined && minPrice !== "") {
      filtered = filtered.filter((p) => {
        const resolved = resolveProductPricing(p, null, 1);
        return Number(resolved.unitPrice || 0) >= Number(minPrice);
      });
    }

    if (maxPrice !== null && maxPrice !== undefined && maxPrice !== "") {
      filtered = filtered.filter((p) => {
        const resolved = resolveProductPricing(p, null, 1);
        return Number(resolved.unitPrice || 0) <= Number(maxPrice);
      });
    }

    if (sort) {
      filtered = [...filtered].sort((a, b) => {
        const isDesc = String(sort).startsWith("-");
        const key = isDesc ? String(sort).slice(1) : String(sort);

        // price sort should respect current pricing logic
        if (key === "price") {
          const aPrice = Number(resolveProductPricing(a, null, 1).unitPrice || 0);
          const bPrice = Number(resolveProductPricing(b, null, 1).unitPrice || 0);
          return isDesc ? bPrice - aPrice : aPrice - bPrice;
        }

        const av = a?.[key];
        const bv = b?.[key];

        if (key === "title" || key === "brand" || key === "slug") {
          const avValue =
            key === "brand"
              ? typeof a?.brand === "object" && a?.brand
                ? a?.brand?.title ?? a?.brand?.name ?? a?.brand?._id ?? ""
                : a?.brand ?? ""
              : av;

          const bvValue =
            key === "brand"
              ? typeof b?.brand === "object" && b?.brand
                ? b?.brand?.title ?? b?.brand?.name ?? b?.brand?._id ?? ""
                : b?.brand ?? ""
              : bv;

          const as = norm(avValue);
          const bs = norm(bvValue);
          if (as < bs) return isDesc ? 1 : -1;
          if (as > bs) return isDesc ? -1 : 1;
          return 0;
        }

        const an = Number(av || 0);
        const bn = Number(bv || 0);
        return isDesc ? bn - an : an - bn;
      });
    }

    dispatch(productSlice.actions.setFilteredProducts(filtered));
  };

/* =========================================================
   Actions + Reducer exports
========================================================= */
export const {
  setFilteredProducts,
  setSearchTerm,
  resetSingleProduct,
  filterProductsLocal,
  resetProductsState,
  setSelectedColor,
  setSelectedQty,
} = productSlice.actions;

export default productSlice.reducer;